// pages/AdminSettings.jsx — Admin Board tab: "My Account"
//
// Lets a signed-in platform admin update their own display name, email, and
// password. Unlike the client app's Settings.jsx "Change Password" card
// (whose fields are still non-functional placeholders — value="" and
// onChange={() => {}}), everything here is wired up for real via
// supabase.auth.updateUser(). That call is safe to make directly from the
// client because it operates on the admin's own current session — no extra
// backend route or admin secret is needed, and it only ever affects the
// person who is signed in (see AdminUsers.jsx for managing *other* admins).
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase';
import { User, Lock, ShieldCheck } from 'lucide-react';
import Icon from '../components/Icon'


import { T, alpha } from '../theme/theme'
const inputStyle = {
  width:'100%', padding:'10px 12px', background:T.s2, border:`1px solid ${T.bd}`,
  borderRadius:'8px', color:T.tx, fontSize:'14px', outline:'none',
  fontFamily:"'Outfit',sans-serif", boxSizing:'border-box', transition:'border-color 0.15s',
}

function Field({ label, ...props }) {
  return (
    <div style={{ marginBottom:'14px' }}>
      <label style={{ display:'block', fontSize:'10px', fontWeight:700, color:T.lt,
        letterSpacing:'0.08em', marginBottom:'6px', textTransform:'uppercase' }}>
        {label}
      </label>
      <input
        {...props}
        style={inputStyle}
        onFocus={e => { e.target.style.borderColor = T.or }}
        onBlur={e  => { e.target.style.borderColor = T.bd }}
      />
    </div>
  )
}

function Card({ icon: Icon, title, desc, children }) {
  return (
    <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'20px', marginBottom:'20px', maxWidth:'560px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom: desc ? '4px' : '16px' }}>
        <Icon size={16} strokeWidth={2} color={T.or} />
        <h3 style={{ fontSize:'15px', fontWeight:800, color:T.tx, fontFamily:"'Syne', sans-serif" }}>{title}</h3>
      </div>
      {desc && <p style={{ fontSize:'13px', fontWeight:'400', color:T.mt, marginBottom:'16px' }}>{desc}</p>}
      {children}
    </div>
  )
}

function SubmitButton({ children, disabled }) {
  return (
    <button
      type="submit"
      disabled={disabled}
      style={{ padding:'10px 20px', borderRadius:'8px', border:'none',
        background: disabled ? T.mt : T.prg,
        color:T.onpri, fontWeight:700, fontSize:'14px', cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily:"'Outfit',sans-serif" }}
    >
      {children}
    </button>
  )
}

const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleString('en-ZA', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })
  : '—'

export default function AdminSettings() {
  const { user } = useAuth();

  // ── Profile (name + email) ──────────────────────────────────
  const [fullName, setFullName]     = useState(user?.user_metadata?.full_name || '');
  const [email, setEmail]           = useState(user?.email || '');
  const [savingProfile, setSaving]  = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  const [profileErr, setProfileErr] = useState('');

  // ── Password ─────────────────────────────────────────────────
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword]   = useState(false);
  const [passwordMsg, setPasswordMsg]         = useState('');
  const [passwordErr, setPasswordErr]         = useState('');

  const emailChanged = email.trim().toLowerCase() !== (user?.email || '').toLowerCase();

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true); setProfileMsg(''); setProfileErr('');
    try {
      if (emailChanged && !email.trim()) {
        setProfileErr('Email cannot be empty.');
        setSaving(false);
        return;
      }
      const updates = { data: { full_name: fullName.trim() } };
      if (emailChanged) updates.email = email.trim();

      const { error } = await supabase.auth.updateUser(updates);
      if (error) throw error;

      setProfileMsg(emailChanged
        ? 'Saved. Check both your old and new inbox for a confirmation link — the email only updates once you click it.'
        : 'Profile updated.');
    } catch (err) {
      setProfileErr(err.message || 'Could not update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg(''); setPasswordErr('');
    if (newPassword.length < 8) {
      setPasswordErr('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordErr('Passwords do not match.');
      return;
    }
    setSavingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setPasswordMsg('Password updated.');
      setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      setPasswordErr(err.message || 'Could not update password. Please try again.');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div>
      <h2 style={{ fontFamily:"'Syne',sans-serif", fontSize:'20px', fontWeight:800, color:T.tx, marginBottom:'4px' }}>
        My Account
      </h2>
      <p style={{ fontSize:'13px', fontWeight:'400', color:T.mt, marginBottom:'24px' }}>
        Update your own name, email, and password for this Admin Board login. This only affects
        you — to manage other admins, see the Admin Users tab.
      </p>

      <Card icon={User} title="Profile" desc="Your name and email as shown across the Admin Board.">
        <form onSubmit={saveProfile}>
          <Field label="Full Name" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="e.g. Thavha Madembe" />
          <Field label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@kailux.co.za" />
          {emailChanged && (
            <p style={{ fontSize:'12px', color:T.am, marginTop:'-6px', marginBottom:'14px' }}>
              <Icon name="alert" /> Changing your email requires confirming a link sent to both the old and new address before it takes effect.
            </p>
          )}
          {profileMsg && <p style={{ fontSize:'13px', color:T.gn, marginBottom:'12px' }}>{profileMsg}</p>}
          {profileErr && <p style={{ fontSize:'13px', color:T.rd, marginBottom:'12px' }}>{profileErr}</p>}
          <SubmitButton disabled={savingProfile}>{savingProfile ? 'Saving…' : 'Save Profile'}</SubmitButton>
        </form>
      </Card>

      <Card icon={Lock} title="Change Password" desc="Choose a new password for signing in at admin.kailux.co.za.">
        <form onSubmit={savePassword}>
          <Field label="New Password" type="password" value={newPassword}
            onChange={e => setNewPassword(e.target.value)} placeholder="Minimum 8 characters" autoComplete="new-password" />
          <Field label="Confirm New Password" type="password" value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat new password" autoComplete="new-password" />
          {passwordMsg && <p style={{ fontSize:'13px', color:T.gn, marginBottom:'12px' }}>{passwordMsg}</p>}
          {passwordErr && <p style={{ fontSize:'13px', color:T.rd, marginBottom:'12px' }}>{passwordErr}</p>}
          <SubmitButton disabled={savingPassword}>{savingPassword ? 'Saving…' : 'Update Password'}</SubmitButton>
        </form>
      </Card>

      <Card icon={ShieldCheck} title="Account Info" desc="Read-only details about this login.">
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px 24px' }}>
          <div>
            <p style={{ fontSize:'10px', fontWeight:700, color:T.mt, letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'4px' }}>Role</p>
            <p style={{ fontSize:'14px', color:T.tx }}>Platform Admin</p>
          </div>
          <div>
            <p style={{ fontSize:'10px', fontWeight:700, color:T.mt, letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'4px' }}>Last Sign-in</p>
            <p style={{ fontSize:'14px', color:T.tx }}>{fmtDate(user?.last_sign_in_at)}</p>
          </div>
          <div>
            <p style={{ fontSize:'10px', fontWeight:700, color:T.mt, letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'4px' }}>Account Created</p>
            <p style={{ fontSize:'14px', color:T.tx }}>{fmtDate(user?.created_at)}</p>
          </div>
          <div>
            <p style={{ fontSize:'10px', fontWeight:700, color:T.mt, letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'4px' }}>User ID</p>
            <p style={{ fontSize:'12px', color:T.mt, fontFamily:"'DM Mono', monospace" }}>{user?.id}</p>
          </div>
        </div>
      </Card>
    </div>
  )
}
